import styled from '@emotion/styled';
import type { FunctionComponent, ReactElement } from 'react';
import { useContext, useEffect } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { Button } from '~/ui/core/button/Button';
import { IconCrossSmall, IconExpand45 } from '~/ui/core/icons';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Tile, TileContent } from '~/ui/core/tile/Tile';
import { Timeline } from '~/ui/core/timeline/Timeline';

import { useMixpanelTracking } from '../../tracking/mixpanel/use-mixpanel-tracking';
import { ContentContainer, StyledStickyHeader } from '../components/Common';
import { ChangelogTimelineSkeleton } from '../components/SkeletonLoaders';
import { TimelineNavigableItem } from './TimelineNavigableItem';
import type { TimelineItem } from './use-changelog-data';
import { useChangelogData } from './use-changelog-data';

const isNavigableItem = (item: TimelineItem): boolean => {
    const isNavigableEntityType = item.entityType === 'role' || item.entityType === 'level';
    const isArchiveEvent = item.eventDetails.type === 'archived' || item.eventDetails.type === 'unarchived';

    return isNavigableEntityType && !isArchiveEvent;
};

export const renderTimelineItem = (item: TimelineItem): ReactElement => {
    if (isNavigableItem(item)) {
        return <TimelineNavigableItem key={item.id} item={item} />;
    }

    return (
        <Timeline.Item key={item.id} icon={item.icon} itemSubtitle={item.subtitle} itemTitle={item.title}>
            <Timeline.Footer>{item.footerItems}</Timeline.Footer>
        </Timeline.Item>
    );
};

const SimpleHeaderContainer = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.m,
    justifyContent: 'flex-start',
    width: '100%',
}));

const ContentHeaderContainer = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.xl,
    justifyContent: 'space-between',
    paddingBottom: theme.spacing['4xl'],
}));

const HeaderTitleSection = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.s,
}));

const Title = styled.h1(({ theme }) => ({
    color: theme.colors.gray[900],
    ...theme.typography.body.L.semibold,
    margin: 0,
}));

const Subtitle = styled.p(({ theme }) => ({
    color: theme.colors.gray[600],
    ...theme.typography.body.S.regular,
    margin: 0,
}));

const TimelineContainer = styled.div(({ theme }) => ({
    paddingBottom: theme.spacing['4xl'],
    width: '100%',
}));

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const LoadingText = styled.div(({ theme }) => ({
    color: theme.colors.gray[600],
    padding: theme.spacing.xl,
    textAlign: 'center',
}));

const SkeletonContainer = styled.div(({ theme }) => ({
    paddingBottom: theme.spacing['4xl'],
    width: '100%',
}));

type OnCloseProps = {
    readonly onClose: () => void;
};

const Header: FunctionComponent<OnCloseProps> = ({ onClose }) => {
    return (
        <StyledStickyHeader>
            <SimpleHeaderContainer>
                <Button aria-label="close" iconOnly={true} size="sm" variant="secondary" onClick={onClose}>
                    <IconCrossSmall />
                </Button>
                <Button disabled={true} iconOnly={true} size="sm" variant="secondary">
                    <IconExpand45 />
                </Button>
            </SimpleHeaderContainer>
        </StyledStickyHeader>
    );
};

type LoaderProps = OnCloseProps & {
    readonly subtitle: string;
    readonly title: string;
};

const Loader: FunctionComponent<LoaderProps> = ({ onClose, subtitle, title }) => {
    return (
        <Tile data-testid="changelog-loader">
            <ScrollArea>
                <TileContent>
                    <ContentContainer>
                        <Container>
                            <Header onClose={onClose} />
                            <ContentHeaderContainer>
                                <HeaderTitleSection>
                                    <Title>{title}</Title>
                                    <Subtitle>{subtitle}</Subtitle>
                                </HeaderTitleSection>
                            </ContentHeaderContainer>
                            <SkeletonContainer>
                                <ChangelogTimelineSkeleton />
                            </SkeletonContainer>
                        </Container>
                    </ContentContainer>
                </TileContent>
            </ScrollArea>
        </Tile>
    );
};

type EmptyChangelogProps = OnCloseProps & {
    readonly message: string;
};
const EmptyChangelog: FunctionComponent<EmptyChangelogProps> = ({ message, onClose }) => {
    return (
        <Tile data-testid="changelog-empty">
            <ScrollArea>
                <TileContent>
                    <ContentContainer>
                        <Container>
                            <Header onClose={onClose} />
                            <LoadingText>{message}</LoadingText>
                        </Container>
                    </ContentContainer>
                </TileContent>
            </ScrollArea>
        </Tile>
    );
};

export const ChangelogDetails: FunctionComponent<{
    readonly onClose: () => void;
}> = ({ onClose }) => {
    const { translate } = useContext(ApplicationContext);
    const { isLoading, LoadMoreCursor, timelineGroups } = useChangelogData();
    const { trackEvent } = useMixpanelTracking();

    useEffect(() => {
        trackEvent({ name: 'changelog-opened', payload: {} });
    }, [trackEvent]);

    if (isLoading && timelineGroups.length === 0) {
        return (
            <Loader
                subtitle={translate('pages.job-architecture-v2.changelog.subtitle')}
                title={translate('pages.job-architecture-v2.changelog.title')}
                onClose={onClose}
            />
        );
    }

    if (!isLoading && timelineGroups.length === 0) {
        return (
            <EmptyChangelog message={translate('pages.job-architecture-v2.changelog.no-entries')} onClose={onClose} />
        );
    }

    return (
        <Tile data-testid="changelog-details">
            <ScrollArea>
                <TileContent>
                    <ContentContainer>
                        <Container>
                            <Header onClose={onClose} />
                            <ContentHeaderContainer>
                                <HeaderTitleSection>
                                    <Title>{translate('pages.job-architecture-v2.changelog.title')}</Title>
                                    <Subtitle>{translate('pages.job-architecture-v2.changelog.subtitle')}</Subtitle>
                                </HeaderTitleSection>
                            </ContentHeaderContainer>
                            <TimelineContainer>
                                {timelineGroups.map((group) => (
                                    <Timeline.Group
                                        key={group.date}
                                        isLastGroup={group.isLastGroup}
                                        label={group.label}
                                    >
                                        {group.items.map(renderTimelineItem)}
                                    </Timeline.Group>
                                ))}
                            </TimelineContainer>
                            {LoadMoreCursor}
                        </Container>
                    </ContentContainer>
                </TileContent>
            </ScrollArea>
        </Tile>
    );
};
